import React, { useImperativeHandle, useState } from 'react';
import { connect } from 'react-redux';
import { FormInstance, Modal, Spin, Form, message, Table, InputNumber } from 'antd';
import { debounce } from 'lodash';
import { ModalProps } from 'antd/lib/modal';
import intl from 'react-intl-universal';
import { uuid } from '@/utils';
import { batchReturnPeripheral } from '@/api/peripheral';
import styles from './index.module.scss';

interface ModalFormProp extends ModalProps {
  title?: string;
  myRef?: any;
  callback?: () => void;
}
export interface IModalRef extends FormInstance {
  closeModal?: () => void;
  openModal?: (params: any) => void;
}

const ModalForm: React.FC<ModalFormProp> = ({
  title = intl.get('goods_periheral_return'),
  myRef,
  callback,
  ...rest
}) => {
  const [loading, setLoading] = useState(false);
  // 显示与隐藏
  const [visible, setVisible] = useState<boolean>();

  const [dataSource, setDataSource] = useState<any[]>([]);
  const [resvNo, setResvNo] = useState('');

  // 确认归还
  const confirmReturn = async () => {
    setLoading(true);
    try {
      const res: any = await batchReturnPeripheral({
        eventNo: uuid(),
        items: dataSource || [],
        resvNo,
      });
      if (res.code === 0) {
        // 如果归还数量都是0 不toast提示
        const isAllQtyZero = (dataSource || []).every((item) => item.qty === 0);
        if (!isAllQtyZero) message.success(intl.get('goods_periheral_return_success'));
        setVisible(false);
        setDataSource([]);
        setResvNo('');
      }
      // callback
      if (callback) {
        callback();
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  // 关闭改价弹框
  const closeModal = () => {
    setVisible(false);

    setDataSource([]);
    setResvNo('');
  };

  // 打开改价弹框
  const openModal = (params: any) => {
    const { returns } = params || {};
    setDataSource(returns);
    setResvNo(params.resvNo);

    setVisible(true);
  };

  // 确定改价
  const handleOk = debounce(() => {
    confirmReturn();
  }, 300);

  const handleCancel = () => {
    closeModal();
  };

  useImperativeHandle(
    myRef,
    () => ({
      closeModal,
      openModal,
    }),
    [],
  );

  // 外设剩余与本次消耗事件
  const changePeripheralConsumeQty = ({
    type,
    skuCode,
    returnQty,
  }: {
    type: 'add' | 'reduce' | 'change';
    skuCode: string;
    returnQty?: number;
  }) => {
    const peripheralList = JSON.parse(JSON.stringify(dataSource)) || [];
    switch (type) {
      case 'add':
        const newList1 = peripheralList.map((item: any) => {
          if (item.skuCode === skuCode) {
            // 达到最大值 消耗大于等于上次剩余
            if (item.qty >= item.roomQty) {
              message.warning(intl.get('cmp_inventory_error_value_max'));
              return item;
            }
            return { ...item, qty: item.qty + 1 };
          }
          return item;
        });
        setDataSource(newList1);
        break;
      case 'reduce':
        const newList2 = peripheralList.map((item) => {
          if (item.skuCode === skuCode) {
            // 达到最小值 剩余数量为0
            if (item.qty === 0) {
              message.warning(intl.get('cmp_inventory_error_value_min'));
              return item;
            }
            return { ...item, qty: item.qty - 1 };
          }
          return item;
        });
        setDataSource(newList2);
        break;
      case 'change':
        const newList3 = peripheralList.map((item) => {
          if (item.skuCode === skuCode) {
            // 达到最大值 上次剩余不为空 消耗数量大于上次剩余
            if (returnQty > item.roomQty) {
              message.warning(intl.get('cmp_inventory_error_value_invalid'));
              return item;
            }
            return { ...item, qty: returnQty };
          }
          return item;
        });
        setDataSource(newList3);
        break;
      default:
        break;
    }
  };

  const inputNumberConfirm = (e: any, record: any) => {
    const val = e.target.value;
    // 非负整数校验
    if (!/(^[0-9]\d*$)/.test(val)) {
      message.warn(intl.get('goods_input_error_and_retry'));
    } else {
      changePeripheralConsumeQty({
        type: 'change',
        skuCode: record.skuCode,
        returnQty: Number(val),
      });
    }
  };

  const columns: any = [
    {
      title: '外设名称',
      align: 'center',
      dataIndex: 'spuName',
    },
    {
      title: '总租借次数',
      align: 'center',
      dataIndex: 'totalTimes',
    },
    {
      title: '房间库存',
      align: 'center',
      dataIndex: 'roomQty',
    },
    {
      title: '归还数量',
      align: 'center',
      dataIndex: 'qty',
      render: (value, v) => {
        return (
          <InputNumber
            value={value}
            className={styles.inputNumber}
            onStep={(val, info) => {
              if (info.type === 'down') {
                changePeripheralConsumeQty({ type: 'reduce', skuCode: v.skuCode });
                return;
              }
              if (info.type === 'up') {
                changePeripheralConsumeQty({ type: 'add', skuCode: v.skuCode });
                return;
              }
              changePeripheralConsumeQty({
                type: 'change',
                skuCode: v.skuCode,
                returnQty: val,
              });
            }}
            onPressEnter={(e) => inputNumberConfirm(e, v)}
            onBlur={(e) => inputNumberConfirm(e, v)}
          />
        );
      },
    },
  ];

  return (
    <>
      <Modal
        title={title}
        destroyOnClose
        forceRender
        maskClosable={false}
        visible={visible}
        onOk={handleOk}
        onCancel={handleCancel}
        className={styles.modal}
        okButtonProps={{ loading }}
        {...rest}
      >
        <Spin spinning={loading}>
          <Table
            rowKey={(record) => record.skuCode}
            columns={columns}
            dataSource={dataSource || []}
            pagination={false}
          />
        </Spin>
      </Modal>
    </>
  );
};

const mapStateToProps = () => {
  return {};
};
const ConnectComponent: React.FC<ModalFormProp> = connect(
  mapStateToProps,
  {},
)(React.memo(ModalForm));

export default React.forwardRef<IModalRef, ModalFormProp>((props, ref) => (
  <ConnectComponent {...props} myRef={ref} />
));
